import React, { useEffect, useState } from 'react';
import {
  Dashboard as DashboardStyled,
  DashboardHeader,
  DashboardTitle,
  DashboardSubtitle,
  StatsGrid,
  StatCard,
  StatIcon,
  StatContent,
  StatValue,
  StatTitle,
  PositiveChange,
  DashboardContent,
  DashboardSection,
  SectionHeader,
  SectionTitle,
  Amount,
  SalesTable,
  SalesTableHeader,
  SalesTableRow,
  EmptyState
} from '../../styles/DashboardStyles';
import { MyAccounts, MyDashboard } from '../../common/CommonApi';


const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalProducts: 0,
    totalSales: 0,
    totalPayments: 0,
    totalBrands: 0,
    activeProducts: 0,
    verifyingProducts: 0,
    totalSizes: 0,
    totalColors: 0,
    activeSizes: 0,
    activeColors: 0,
    verifyingSizes: 0,
    verifyingColors: 0,
    salesMonthWise: []
  });
  const [isVendor, setIsVendor] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const [response, myAccounts] = await Promise.all([
        MyDashboard(),
        MyAccounts()
      ]);

      setIsVendor(Boolean(myAccounts?.isVendor));
      setStats({
        totalUsers: response?.totalUsers || 0,
        totalOrders: response?.totalOrders || 0,
        totalRevenue: response?.totalRevenue || 0,
        totalProducts: response?.totalProducts || 0,
        totalSales: response?.totalSales || response?.totalRevenue || 0,
        totalPayments: response?.totalPayments || response?.totalPayment || 0,
        totalBrands: response?.totalBrands || 0,
        activeProducts: response?.activeProducts || 0,
        verifyingProducts: response?.verifyingProducts || 0,
        totalSizes: response?.totalSizes || 0,
        totalColors: response?.totalColors || 0,
        activeSizes: response?.activeSizes || 0,
        activeColors: response?.activeColors || 0,
        verifyingSizes: response?.verifyingSizes || 0,
        verifyingColors: response?.verifyingColors || 0,
        salesMonthWise: Array.isArray(response?.salesMonthWise) ? response.salesMonthWise : []
      });
    } catch (error) {
      setStats({
        totalUsers: 0,
        totalOrders: 0,
        totalRevenue: 0,
        totalProducts: 0,
        totalSales: 0,
        totalPayments: 0,
        totalBrands: 0,
        activeProducts: 0,
        verifyingProducts: 0,
        totalSizes: 0,
        totalColors: 0,
        activeSizes: 0,
        activeColors: 0,
        verifyingSizes: 0,
        verifyingColors: 0,
        salesMonthWise: []
      });
    } finally {
      setLoading(false);
    }
  };

  const currency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN")}`;

  const monthLabel = (year, month) =>
    new Date(year, month - 1).toLocaleString("en-IN", {
      month: "short",
      year: "numeric"
    });

  const adminStats = [
    {
      title: "Total Users",
      value: stats.totalUsers || 0,
      icon: "👥"
    },
    {
      title: "Total Orders",
      value: stats.totalOrders || 0,
      icon: "🛒"
    },
    {
      title: "Revenue",
      value: currency(stats.totalRevenue),
      icon: "₹"
    },
    {
      title: "Products",
      value: stats.totalProducts || 0,
      icon: "📦"
    }
  ];

  const vendorStats = [
    {
      title: "Total Orders",
      value: stats.totalOrders || 0,
      icon: "🛒"
    },
    {
      title: "Total Sales",
      value: currency(stats.totalSales),
      icon: "₹"
    },
    {
      title: "Total Payments",
      value: currency(stats.totalPayments),
      icon: "💳"
    },
    {
      title: "Total Brands",
      value: stats.totalBrands || 0,
      icon: "🏷️"
    },
    {
      title: "Active Products",
      value: stats.activeProducts || 0,
      icon: "✅"
    },
    {
      title: "Verifying Products",
      value: stats.verifyingProducts || 0,
      icon: "⏳"
    },
    {
      title: "Active Sizes",
      value: stats.activeSizes || 0,
      icon: "↕"
    },
    {
      title: "Active Colors",
      value: stats.activeColors || 0,
      icon: "🎨"
    },
    {
      title: "Verifying Sizes",
      value: stats.verifyingSizes || 0,
      icon: "↕"
    },
    {
      title: "Verifying Colors",
      value: stats.verifyingColors || 0,
      icon: "🎨"
    }
  ];

  const dashboardStats = isVendor ? vendorStats : adminStats;

  return (
    <DashboardStyled>
      <DashboardHeader>
        <DashboardTitle>Welcome back!</DashboardTitle>
        <DashboardSubtitle>
          {isVendor
            ? "Here's what's happening with your store today."
            : "Here's what's happening across the platform today."}
        </DashboardSubtitle>
      </DashboardHeader>

      <StatsGrid>
       {!loading && dashboardStats.length > 0 && (
          dashboardStats.map((stat, index) => (
            <StatCard key={`${stat.title}-${index}`}>
              <StatIcon>{stat.icon}</StatIcon>
              <StatContent>
                <StatValue>{stat.value}</StatValue>
                <StatTitle>{stat.title}</StatTitle>
                {stat.change && <PositiveChange>{stat.change}</PositiveChange>}
              </StatContent>
            </StatCard>
          ))
        )}
      </StatsGrid>

      {isVendor && !loading && (
        <DashboardContent>
          <DashboardSection>
            <SectionHeader>
              <SectionTitle>Sales Month Wise</SectionTitle>
            </SectionHeader>

            {stats.salesMonthWise.length > 0 ? (
              <SalesTable>
                <SalesTableHeader>
                  <div>Month</div>
                  <div>Orders</div>
                  <div>Sales</div>
                </SalesTableHeader>
                {stats.salesMonthWise.map((sale) => (
                  <SalesTableRow key={`${sale.year}-${sale.month}`}>
                    <div>{monthLabel(sale.year, sale.month)}</div>
                    <div>{sale.orders || 0}</div>
                    <Amount>{currency(sale.sales)}</Amount>
                  </SalesTableRow>
                ))}
              </SalesTable>
            ) : (
              <EmptyState>No sales data available yet.</EmptyState>
            )}
          </DashboardSection>

          <DashboardSection>
            <SectionHeader>
              <SectionTitle>Product Variants</SectionTitle>
            </SectionHeader>
            <SalesTable>
              <SalesTableHeader>
                <div>Status</div>
                <div>Sizes</div>
                <div>Colors</div>
              </SalesTableHeader>
              <SalesTableRow>
                <div>Active</div>
                <div>{stats.activeSizes || 0}</div>
                <div>{stats.activeColors || 0}</div>
              </SalesTableRow>
              <SalesTableRow>
                <div>Verifying</div>
                <div>{stats.verifyingSizes || 0}</div>
                <div>{stats.verifyingColors || 0}</div>
              </SalesTableRow>
              <SalesTableRow>
                <div>Total</div>
                <div>{stats.totalSizes || 0}</div>
                <div>{stats.totalColors || 0}</div>
              </SalesTableRow>
            </SalesTable>
          </DashboardSection>
        </DashboardContent>
      )}
    </DashboardStyled>
  );
};

export default Dashboard;
